import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';

/**
 * The player's view of a training program, as slots rather than as documents.
 *
 * A program buys N lessons but creates lesson documents on demand —
 * `appendLessonToOrder` pushes onto `order.lessonIds` as each is submitted, and
 * an order can start with `lessonIds: []`. So a four-lesson program routinely
 * has one or two documents, and the screen has to render four rows regardless.
 * `order.totalLessons` is the source of truth for how many slots exist; the
 * documents fill in the ones that have happened.
 */

export type SlotState =
  | 'upcoming'          // no document yet, and not the player's turn
  | 'ready_to_submit'   // the player's turn
  | 'uploading'         // bytes in flight
  | 'processing'        // in Storage, Cloud Run transcoding
  | 'awaiting_review'   // submitted, with the coach
  | 'ready_to_watch'    // delivered, unwatched
  | 'complete'          // delivered, watched
  | 'refunded';

export interface ProgramSlot {
  /** 1-based, and the number shown to the player. */
  number: number;
  /** Absent for an upcoming slot, which is the common case beyond lesson 2. */
  lesson: Lesson | null;
  state: SlotState;
  /** Upload progress 0-100. Only meaningful while `uploading`. */
  progress?: number;
}

/** The status labels are the app's existing vocabulary, so LessonStatusIcon
 *  maps them to the same mark this state gets everywhere else. */
export const SLOT_LABEL: Record<SlotState, string> = {
  upcoming: 'Upcoming',
  ready_to_submit: 'Ready to submit',
  uploading: 'Uploading',
  processing: 'Processing',
  awaiting_review: 'Awaiting analysis',
  ready_to_watch: 'Ready to watch',
  complete: 'Complete',
  refunded: 'Refunded',
};

/**
 * The one-at-a-time rule, lifted verbatim from BundleLessonDetail's
 * `showUploadCta` so there is one definition rather than two that can drift.
 * The expression is unchanged; only its home moved.
 *
 * Reading it: a player may submit when nothing of theirs is already uploading,
 * AND either this lesson is reserved and waiting for its video, or this is the
 * most recent delivered lesson, there is no next lesson already with the coach,
 * the program has not finished and it has not been refunded.
 */
export function canUploadInProgram(a: {
  hasBlockingUpload: boolean;
  isReserved: boolean;
  hasActivePendingUploadForThisLesson: boolean;
  isFinalLesson: boolean;
  inProgressExists: boolean;
  isMostRecent: boolean;
  isRefunded: boolean;
}): boolean {
  return !a.hasBlockingUpload && (
    (a.isReserved && !a.hasActivePendingUploadForThisLesson) ||
    (!a.isFinalLesson && !a.inProgressExists && a.isMostRecent && !a.isRefunded)
  );
}

/** First frame of the player's submission, or null when no strip was generated
 *  — reserved lessons have no video, and lessons predating the preprocessor
 *  have no thumbnails. */
export function firstFramePath(lesson?: Lesson | null): string | null {
  const base = lesson?.playerVideoThumbnailsBasePath;
  if (!base || (lesson?.playerVideoThumbnailCount ?? 0) === 0) return null;
  return `${base}/0.jpg`;
}

/**
 * Build one slot per lesson the program bought.
 *
 * `pendingFor` reports an in-flight upload for a given lesson id, and
 * `pendingForNextSlot` the one that has no lesson document yet — an upload
 * started from a slot creates its lesson only when the function finalises it,
 * so until then the row has a percentage and no document behind it.
 */
export function buildProgramSlots(args: {
  order: Order;
  lessons: Lesson[];
  hasBlockingUpload: boolean;
  pendingFor: (lessonId: string) => { status: string; progress: number } | null;
  pendingUnattached: { status: string; progress: number } | null;
}): ProgramSlot[] {
  const { order, lessons, hasBlockingUpload, pendingFor, pendingUnattached } = args;

  const total = order.totalLessons ?? Math.max(lessons.length, 1);
  const isRefunded = order.refundRequested === true || order.paymentStatus === 'refunded';
  const byNumber = new Map<number, Lesson>();
  lessons.forEach((l, i) => byNumber.set(l.lessonNumber ?? i + 1, l));

  const delivered = lessons.filter((l) => l.status === 'delivered');
  const inProgressExists = lessons.some((l) => l.status === 'in_progress');
  const mostRecentDeliveredNumber = delivered.length
    ? Math.max(...delivered.map((l, i) => l.lessonNumber ?? i + 1))
    : 0;

  const slots: ProgramSlot[] = [];
  for (let n = 1; n <= total; n++) {
    const lesson = byNumber.get(n) ?? null;
    const pending = lesson ? pendingFor(lesson.id) : null;

    if (isRefunded && lesson?.status !== 'delivered') {
      /* Delivered lessons stay watchable after a refund request — the player
         keeps what they were already sent. Everything unstarted is off. */
      slots.push({ number: n, lesson, state: 'refunded' });
      continue;
    }

    if (lesson?.status === 'delivered') {
      slots.push({ number: n, lesson, state: lesson.playerViewedAt ? 'complete' : 'ready_to_watch' });
      continue;
    }

    /* `playerVideoUrl` set while the status is still 'reserved' means the
       function has not flipped it yet. Treat it as submitted, or the row
       invites a second upload of a video that already landed. */
    const submitted = lesson?.status === 'in_progress' || !!lesson?.playerVideoUrl;
    if (lesson && submitted) {
      slots.push({ number: n, lesson, state: 'awaiting_review' });
      continue;
    }

    const inFlight = pending ?? (lesson ? null : (n === mostRecentDeliveredNumber + 1 ? pendingUnattached : null));
    if (inFlight) {
      slots.push({
        number: n,
        lesson,
        state: inFlight.status === 'uploading' ? 'uploading' : 'processing',
        progress: inFlight.progress,
      });
      continue;
    }

    const isReserved = lesson?.status === 'reserved';
    const isTheNextOne = !lesson && n === mostRecentDeliveredNumber + 1;
    const canUpload = canUploadInProgram({
      hasBlockingUpload,
      isReserved,
      hasActivePendingUploadForThisLesson: false,
      /* Anchored on the slot rather than on the delivered lesson the CTA used
         to hang off: `!isFinalLesson` there meant "there is a next lesson to
         upload", which here is "this slot exists within the program". */
      isFinalLesson: !isTheNextOne,
      inProgressExists,
      isMostRecent: isTheNextOne,
      isRefunded,
    });

    slots.push({ number: n, lesson, state: canUpload ? 'ready_to_submit' : 'upcoming' });
  }

  return slots;
}
